import {
	Autocomplete,
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	TextField,
	Typography,
	Divider
} from "@mui/material";
import React, { FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import "./addVisitorDialog.css";
import { useAddEventVisitorMutation, useAddVisitorMutation } from "../../../redux/reducers/dashboard.reducer";
import { ReactComponent as CarIcon } from "../../../assets/icons/car-icon.svg";
import { ReactComponent as BikeIcon } from "../../../assets/icons/bike-icon.svg";
import { BUILDING_TYPES_E, CONTACT_NUMBER_REGEX, NUMBER_PLATE_REGEX } from "../../../utils/constants";
import {
	useGetCamerasListQuery,
	useGetRegisteredFlatsQuery,
	useGetSocietyConfigQuery
} from "../../../redux/reducers/settings.reducer";
import { IVehicleEventDetails } from "..";
import moment from "moment";
import NumberInput from "../../../common/NumberInput";
import { IAddEventVisitrRequestData, IAddVisitorRequestData, ISocietyDetails } from "../../../interfaces";
import { convertToSnakeCase } from "../../../utils/commonUtils";

interface IAddVisitorDialogProps {
	open: boolean;
	selectedEvent: IVehicleEventDetails | null;
	onClose: () => void;
}

interface IFlatDetails {
	key: string;
	building: string;
	flatNumber: string;
}

const AddVisitorDialog: FC<IAddVisitorDialogProps> = (props) => {
	const { open, selectedEvent, onClose } = props;

	const [selectedVehicleType, setSelectedVehicleType] = useState<number>(0);

	const [vehicleTypeDropdownAnchorElement, setVehicleTypeDropdownAnchorElement] = useState<HTMLElement | null>(null);
	const vehicleTypeDropdownOpen = Boolean(vehicleTypeDropdownAnchorElement);

	// APIS
	// const { data: getVisitTypesResponse } = useGetVisitTypesQuery();
	// const visitTypes = getVisitTypesResponse?.results ?? [];

	const { data: getCamerasListResponse } = useGetCamerasListQuery({});
	const camerasList = getCamerasListResponse?.results ?? [];

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyDetails: ISocietyDetails | null =
		getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
			? getSocietyConfigResponse.results[0]
			: null;

	const [
		addVisitor,
		{ isLoading: addVisitorLoading, error: addVisitorError, isSuccess: addVisitorSuccess, reset: resetAddVisitorState }
	] = useAddVisitorMutation();

	const [
		addEventVisitor,
		{
			isLoading: addEventVisitorLoading,
			error: addEventVisitorError,
			isSuccess: addEventVisitorSuccess,
			reset: resetAddEventVisitor
		}
	] = useAddEventVisitorMutation();

	const { data: registeredFlats } = useGetRegisteredFlatsQuery();

	const FlatsList = useMemo(() => {
		const flatsList: IFlatDetails[] = [];

		if (registeredFlats) {
			const customFlats: IFlatDetails[] = [];

			for (const [buildingName, buildingDetails] of Object.entries(registeredFlats.detail)) {
				for (const flatItem of buildingDetails.entries) {
					let key = flatItem;

					if (buildingDetails.building_type === BUILDING_TYPES_E.BUILDING) {
						key = `${buildingName}${buildingDetails.delimiter || "-"}${flatItem}`;
					}

					const flatDetails: IFlatDetails = {
						key: key,
						building: buildingName,
						flatNumber: flatItem
					};

					if (buildingDetails.is_custom_flat_numbers) {
						customFlats.push({ ...flatDetails, building: "Custom Units" });
					} else {
						flatsList.push(flatDetails);
					}
				}
			}

			flatsList.push(...customFlats);
		}

		return flatsList;
	}, [registeredFlats]);

	const {
		register,
		handleSubmit,
		reset,
		control,
		formState: { errors }
	} = useForm();

	// const purposeOfVisit = watch("purpose_of_visit");

	// const flatVisitTypeId = visitTypes?.find((item) => item.type_of === "Member")?.id ?? -1;

	function handleOpenVehicleTypeDropdown(event: MouseEvent<HTMLDivElement>) {
		setVehicleTypeDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseVehicleTypeDropdown() {
		setVehicleTypeDropdownAnchorElement(null);
	}

	function handleChangeSelectedVehicleType(updatedValue: number) {
		setSelectedVehicleType(updatedValue);
		handleCloseVehicleTypeDropdown();
	}

	function handleSubmitAddVisitorForm(data: FieldValues) {
		const selectedLocation = camerasList.find((cameraItem) => cameraItem.id === Number(data.location));

		const customFieldsRequestData: { [key in string]: string } = {};

		if (societyDetails && societyDetails.custom_fields && societyDetails.custom_fields.length > 0) {
			for (const customFieldItem of societyDetails.custom_fields) {
				const key = convertToSnakeCase(customFieldItem.name);
				customFieldsRequestData[customFieldItem.name] = data[key];
			}
		}

		if (selectedEvent) {
			const requestData: IAddEventVisitrRequestData = {
				id: selectedEvent.id,
				data: {
					number_plate: String(data.vehicle_number).toUpperCase(),
					auth: selectedEvent.type === "authorized" ? true : false,
					is_entry: selectedLocation?.is_entry ?? selectedEvent.is_entry ?? false,
					vehicle_type: selectedVehicleType,
					camera: selectedLocation ? selectedLocation.id : 0,
					visitor: {
						name: data.name,
						phone_number: data.contact_number ? data.contact_number : null,
						address: data.address ? data.address : null,
						purpose: data.comment ?? "",
						// visit_type: Number(data.purpose_of_visit),
						flat_number: data.flat_number ? data.flat_number.key : null,
						is_custom_flat_number: data.flat_number && data.flat_number.building === "Custom Units" ? true : false
					}
				}
			};

			if (Object.keys(customFieldsRequestData).length > 0) {
				requestData.data.visitor.custom_field_data = customFieldsRequestData;
			}

			addEventVisitor(requestData);
		} else {
			const requestData: IAddVisitorRequestData = {
				number_plate: String(data.vehicle_number).toUpperCase(),
				auth: false,
				is_entry: selectedLocation?.is_entry ?? false,
				vehicle_type: selectedVehicleType,
				// unique_id: selectedEvent ? selectedEvent.unique_id : null,
				image: "",
				society: 1,
				is_blacklisted: false,
				timestamp: moment().toISOString(),
				camera: selectedLocation ? selectedLocation.id : 0,
				visitor: {
					name: data.name,
					phone_number: data.contact_number ? data.contact_number : null,
					address: data.address ? data.address : null,
					purpose: data.comment ?? "",
					// visit_type: Number(data.purpose_of_visit),
					flat_number: data.flat_number ? data.flat_number.key : null,
					is_custom_flat_number: data.flat_number && data.flat_number.building === "Custom Units" ? true : false
				}
			};

			if (Object.keys(customFieldsRequestData).length > 0) {
				requestData.visitor.custom_field_data = customFieldsRequestData;
			}

			addVisitor(requestData);
		}
	}

	useEffect(() => {
		if (open) {
			if (selectedEvent) {
				setSelectedVehicleType(selectedEvent.vehicle_type);

				const selectedFlatNumber = FlatsList.find((item) => item.key === selectedEvent.flat_number);

				reset({
					name: selectedEvent.name ?? "",
					contact_number: "",
					location: selectedEvent.site_id ?? "",
					vehicle_number: selectedEvent.vehicle_number ?? "",
					purpose_of_visit: "",
					flat_number: selectedFlatNumber ? selectedFlatNumber : null,
					address: ""
				});
			} else {
				setSelectedVehicleType(0);

				reset({
					name: "",
					contact_number: "",
					location: "",
					vehicle_number: "",
					purpose_of_visit: "",
					flat_number: null,
					address: ""
				});
			}
		} else {
			resetAddVisitorState();
			resetAddEventVisitor();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, selectedEvent]);

	useEffect(() => {
		if (addVisitorSuccess) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addVisitorSuccess]);

	useEffect(() => {
		if (addEventVisitorSuccess) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addEventVisitorSuccess]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
					color: "white",
					position: "relative",
					padding: "16px 20px 12px 20px",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<PersonAddIcon sx={{ fontSize: "1.5rem" }} />
					<Typography variant="h6" sx={{ fontWeight: 600 }}>
						Add New Visitor
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: 12, right: 12 }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: "6px",
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					padding: "20px !important",
					background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)"
				}}
			>
				<Grid container spacing={2} component="form" noValidate onSubmit={handleSubmit(handleSubmitAddVisitorForm)}>
					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="name-input"
							required
							error={!!errors.name}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="name-input"
							placeholder="Enter the name of person"
							autoComplete="off"
							error={!!errors.name}
							helperText={errors.name && errors.name.message ? errors.name.message.toString() : ""}
							{...register("name", { required: "Name is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="contact-number-input"
							error={!!errors.contact_number}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Contact Number
						</InputLabel>

						<NumberInput
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							isContactNumber
							variant="outlined"
							color="primary"
							size="small"
							id="contact-number-input"
							placeholder="Enter the contact number"
							error={!!errors.contact_number}
							helperText={
								errors.contact_number && errors.contact_number.message ? errors.contact_number.message.toString() : ""
							}
							{...register("contact_number", {
								pattern: { value: CONTACT_NUMBER_REGEX, message: "Enter a valid contact number" },
								min: { value: 0, message: "Invalid contact number" }
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="address-input"
							error={!!errors.address}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Address
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							placeholder="Enter the address"
							autoComplete="off"
							error={!!errors.address}
							helperText={errors.address && errors.address.message ? errors.address.message.toString() : ""}
							{...register("address")}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="location-input"
							error={!!errors.location}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Location
						</InputLabel>

						<Controller
							name="location"
							control={control}
							rules={{ required: "Location is required" }}
							defaultValue=""
							render={({ field: { value, onChange } }) => (
								<Autocomplete
									fullWidth
									options={camerasList}
									getOptionLabel={(option) => option.site_name}
									getOptionKey={(option) => option.id}
									sx={{ height: "100%" }}
									id="location-input"
									value={camerasList.find((item) => item.id === value) ?? null}
									onChange={(_, updatedValue) => onChange(updatedValue?.id ?? null)}
									renderInput={(params) => (
										<TextField
											{...params}
											required
											fullWidth
											hiddenLabel
											variant="outlined"
											color="primary"
											size="small"
											placeholder="Select the location"
											autoComplete="off"
											error={!!errors.location}
											helperText={errors.location && errors.location.message ? errors.location.message.toString() : ""}
										/>
									)}
								/>
							)}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box className="vehicle-details-wrapper">
							<Box sx={{ flexGrow: 1 }}>
								<InputLabel
									required
									htmlFor="vehicle-input-input"
									error={!!errors.vehicle_number}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: "0.875rem"
									}}
								>
									Vehicle Number
								</InputLabel>

								<TextField
									required
									fullWidth
									variant="outlined"
									color="primary"
									size="small"
									id="vehicle-number-input"
									placeholder="Enter the vehicle number"
									autoComplete="off"
									error={!!errors.vehicle_number}
									helperText={
										errors.vehicle_number && errors.vehicle_number.message
											? errors.vehicle_number.message.toString()
											: ""
									}
									{...register("vehicle_number", {
										required: "Vehicle number is required",
										pattern: { value: NUMBER_PLATE_REGEX, message: "Enter a valid number plate" }
									})}
								/>
							</Box>

							<Box
								className="vehicle-type-dropdown-wrapper"
								component="div"
								aria-haspopup="true"
								id="vehicle-type-dropdown-button"
								onClick={handleOpenVehicleTypeDropdown}
								aria-expanded={vehicleTypeDropdownOpen ? "true" : undefined}
								aria-controls={vehicleTypeDropdownOpen ? "vehicle-type-dropdown-menu" : undefined}
								sx={{
									background: "linear-gradient(135deg, rgba(91, 78, 191, 0.1) 0%, rgba(91, 78, 191, 0.05) 100%)",
									border: "1px solid rgba(91, 78, 191, 0.2)",
									borderRadius: "8px",
									transition: "all 0.2s ease-in-out",
									"&:hover": {
										background: "linear-gradient(135deg, rgba(91, 78, 191, 0.15) 0%, rgba(91, 78, 191, 0.08) 100%)",
										transform: "scale(1.05)"
									}
								}}
							>
								{selectedVehicleType === 0 ? (
									<CarIcon width={24} fill="#5b4ebf" />
								) : (
									<BikeIcon width={24} fill="#5b4ebf" />
								)}

								<Box
									sx={{
										position: "absolute",
										bottom: 0,
										left: 0,
										right: 0,
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}
								>
									<ArrowDropDownIcon sx={{ color: "#5b4ebf", fontSize: "1rem" }} />
								</Box>
							</Box>

							<Menu
								id="vehicle-type-dropdown-menu"
								anchorEl={vehicleTypeDropdownAnchorElement}
								open={vehicleTypeDropdownOpen}
								onClose={handleCloseVehicleTypeDropdown}
								MenuListProps={{ "aria-labelledby": "vehicle-type-dropdown-button", disablePadding: true }}
								slotProps={{ paper: { elevation: 2 } }}
								sx={{
									"& .MuiPaper-root": {
										borderRadius: "8px",
										border: "1px solid rgba(91, 78, 191, 0.1)",
										background: "rgba(255, 255, 255, 0.95)",
										backdropFilter: "blur(8px)"
									}
								}}
							>
								<MenuItem
									onClick={() => handleChangeSelectedVehicleType(0)}
									sx={{
										padding: "8px 12px",
										height: "48px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											background: "rgba(91, 78, 191, 0.08)"
										}
									}}
								>
									<CarIcon width={24} fill="#5b4ebf" />
								</MenuItem>

								<MenuItem
									onClick={() => handleChangeSelectedVehicleType(1)}
									sx={{
										padding: "8px 12px",
										height: "48px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											background: "rgba(91, 78, 191, 0.08)"
										}
									}}
								>
									<BikeIcon width={24} fill="#5b4ebf" />
								</MenuItem>
							</Menu>
						</Box>
					</Grid>

					{FlatsList.length > 0 ? (
						<Grid item xs={12} md={6}>
							<InputLabel
								htmlFor="flat-number-input"
								error={!!errors.flat_number}
								sx={{
									fontWeight: 600,
									color: "#3D3D3D",
									marginBottom: "0.5rem",
									fontSize: "0.875rem"
								}}
							>
								Area of Visit
							</InputLabel>

							<Controller
								name="flat_number"
								control={control}
								defaultValue=""
								render={({ field: { value, onChange } }) => (
									<Autocomplete
										fullWidth
										options={FlatsList}
										getOptionLabel={(option) => option.key}
										getOptionKey={(option) => option.key}
										groupBy={(option) => option.building}
										sx={{ height: "100%" }}
										value={value ? value : null}
										onChange={(_, updatedValue) => onChange(updatedValue ?? null)}
										id="flat-number-input"
										renderInput={(params) => (
											<TextField
												{...params}
												fullWidth
												hiddenLabel
												variant="outlined"
												size="small"
												placeholder="Enter the area of visit"
												autoComplete="off"
												error={!!errors.flat_number}
												helperText={
													errors.flat_number && errors.flat_number.message ? errors.flat_number.message.toString() : ""
												}
											/>
										)}
									/>
								)}
							/>
						</Grid>
					) : null}

					{societyDetails && societyDetails.custom_fields && societyDetails.custom_fields.length > 0
						? societyDetails.custom_fields.map((customFieldItem) => {
								const key = convertToSnakeCase(customFieldItem.name);
								return (
									<Grid item xs={12} md={6} key={key}>
										<InputLabel
											htmlFor={`${key}-input`}
											error={!!errors.address}
											sx={{
												fontWeight: 600,
												color: "#3D3D3D",
												marginBottom: "0.5rem",
												fontSize: "0.875rem"
											}}
										>
											{customFieldItem.name}
										</InputLabel>

										{customFieldItem.type === "dropdown" ? (
											<Controller
												name={key}
												control={control}
												defaultValue=""
												render={({ field: { value, onChange } }) => (
													<Autocomplete
														fullWidth
														options={customFieldItem.entries}
														sx={{ height: "100%" }}
														value={value}
														onChange={(_, updatedValue) => onChange(updatedValue ?? null)}
														id={`${key}-input`}
														renderInput={(params) => (
															<TextField
																{...params}
																fullWidth
																hiddenLabel
																variant="outlined"
																size="small"
																autoComplete="off"
																placeholder={`Enter the ${customFieldItem.name}`}
																error={!!errors[key]}
																helperText={errors[key] && errors[key]?.message ? errors[key]?.message?.toString() : ""}
															/>
														)}
													/>
												)}
											/>
										) : customFieldItem.type === "custom" ? (
											<TextField
												fullWidth
												variant="outlined"
												color="primary"
												size="small"
												autoComplete="off"
												id={`${key}-input`}
												placeholder={`Enter the ${customFieldItem.name}`}
												error={!!errors[key]}
												helperText={errors[key] && errors[key]?.message ? errors[key]?.message?.toString() : ""}
												{...register(key)}
											/>
										) : null}
									</Grid>
								);
						  })
						: null}

					{(addVisitorError || addEventVisitorError) && (
						<Grid item xs={12}>
							<Box
								sx={{
									padding: "12px 16px",
									background: "linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.04) 100%)",
									borderRadius: "8px",
									border: "1px solid rgba(211, 47, 47, 0.1)",
									marginTop: 1
								}}
							>
								<Typography variant="body2" color="#d32f2f" textAlign="center" sx={{ fontWeight: 500 }}>
									{addVisitorError && "data" in addVisitorError
										? `${String(addVisitorError.data).slice(0, 1).toUpperCase()}${String(addVisitorError.data).slice(
												1
										  )}`
										: addEventVisitorError && "data" in addEventVisitorError
										? `${String(addEventVisitorError.data).slice(0, 1).toUpperCase()}${String(
												addEventVisitorError.data
										  ).slice(1)}`
										: "Something went wrong"}
								</Typography>
							</Box>
						</Grid>
					)}

					<Grid item xs={12}>
						<Divider sx={{ margin: "16px 0", borderColor: "rgba(0, 0, 0, 0.08)" }} />
						<Box
							sx={{
								width: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<LoadingButton
								variant="contained"
								color="primary"
								type="submit"
								loading={addVisitorLoading || addEventVisitorLoading}
								sx={{
									background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
									borderRadius: "8px",
									padding: "8px 32px",
									fontWeight: 600,
									minWidth: "140px",
									"&:hover": {
										background: "linear-gradient(135deg, #403685 0%, #5b4ebf 100%)",
										boxShadow: "0px 4px 12px rgba(91, 78, 191, 0.3)",
										transform: "translateY(-1px)"
									},
									transition: "all 0.2s ease-in-out"
								}}
							>
								Save Visitor
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default AddVisitorDialog;
